import React from "react";

import { Box, Card, CardContent, Container, Grid, Typography } from "@mui/material";

import YouTubeIcon from "@mui/icons-material/YouTube";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";

import CustomInput from "../../../../helper-components/input/CustomInput";
import CustomStepper from "../../../../helper-components/form/CustomStepper";
import ConfirmationDialog from "../../../../helper-components/input/ConfirmationDialog";
import TransparentFragment from "../../../../helper-components/misc/TransparentFragment";

import { useDialog } from "../../../../helper-functions/hooks/useDialog";
import { queryCookieStepper } from "../../../../helper-functions/api/cookieQueries";
import { LIBRARY_VALIDATION } from "../../../../helper-functions/validation/libraryValidation";
import { addFormToObj, formatDate, isEmpty, isYoutube, setInsertObjectState, setValue } from "../../../../helper-functions/shared/miscUtils";
import { formHasErrors, formHasValue, validateRow } from "../../../../helper-functions/validation/objectValidation";

import { COOKIE_REQUEST_OBJECT, COOKIE_TYPES, LIBRARY_REQUEST_OBJECT } from "../../../../config/constants/Constants";

/**
 * A stepper component to add a new library.
 * Includes helper descriptions and validation.
 */
export default function LibraryAddStepper(props) {
    const { enqueueDialog, setOpen, ...options } = useDialog(); // Nested dialog component.

    const status = props.backend.programStatus;

    // Because modals don't re-render if the props' state changes, use a callback.
    const { callback, row: existingRow = {} } = props;

    const object = LIBRARY_REQUEST_OBJECT;
    const validation = LIBRARY_VALIDATION;

    const [row, setRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(object))));

    // Source selection state
    const [sourceType, setSourceType] = React.useState(""); // "youtube", "vimeo"

    // Cookie state logic.
    const cookieObject = COOKIE_REQUEST_OBJECT;

    const [recentCookie, setRecentCookie] = React.useState({});
    const [cookieRow, setCookieRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(cookieObject))));

    // Initialize the temporary row object with existing values.
    React.useEffect(() => {
        const newRow = {
            ...row,
            ...existingRow,

            form: {
                ...row.form,
                ...existingRow.form,
                saved: false // Required to allow editing input fields.
            }
        };

        setInsertObjectState(setRow, newRow);
    }, []);

    // Automatically set the cookie type.
    React.useEffect(() => {
        if (isYoutube(row?.url)) {
            onCookieChange(cookieRow, "type", "YOUTUBE.COM");
        }
    }, [row]);

    // Save the latest cookie object.
    React.useEffect(() => {
        if (!cookieRow?.type) return;

        const type = cookieRow.type;
        if (recentCookie[type]) return;

        queryCookieStepper(props, { type }, (response) => {
            setRecentCookie((prevState) => ({
                ...prevState,
                [type]: response
            }));
        });
    }, [cookieRow]);

    /**
     * Executed when typing something into an input field.
     */
    const onChange = (obj, key, value) => {
        const newRow = JSON.parse(JSON.stringify(row));
        const newValue = ["url"].includes(key) && value ? value.trim() : value;

        setValue(newRow, key, newValue);

        validateRow(obj, validation, newRow);
        setInsertObjectState(setRow, newRow);
    };

    const handleClick = (confirm) => {
        const options = { row, cookieRow };

        callback(confirm, options);
    };

    const onCookieChange = (obj, key, value) => {
        const newRow = JSON.parse(JSON.stringify(cookieRow));
        setValue(newRow, key, value);

        setInsertObjectState(setCookieRow, newRow);
    };

    // Handle source type selection
    const handleSourceSelect = (type) => {
        setSourceType(type);

        switch (type) {
            case "youtube":
                // Default prefix for YouTube URLs
                onChange(row, "url", "");
                break;
            case "vimeo":
                // Default prefix for Vimeo URLs
                onChange(row, "url", "");
                break;
            default:
                break;
        }
    };

    // Create the source selection step
    const step1 = () => {
        const sourceCardStyle = {
            cursor: "pointer",
            height: "110px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s",
            "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)"
            },
            border: "2px solid #e0e0e0",
            position: "relative"
        };

        const createCard = (icon, title, subtitle, color, disabled = false) => (
            <Card
                sx={{
                    ...sourceCardStyle,
                    opacity: disabled ? 0.6 : 1,
                    cursor: disabled ? "not-allowed" : "pointer"
                }}
                onClick={() => {
                    if (!disabled) handleSourceSelect(title.toLowerCase());
                }}
                style={{
                    borderColor: sourceType === title.toLowerCase() ? color : "#e0e0e0"
                }}>
                <CardContent>
                    <Box sx={{ textAlign: "center" }}>
                        {icon}

                        <Typography variant="body" component="div" sx={{ mt: 1 }}>
                            {title}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            {subtitle}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );

        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TransparentFragment value={`Select the source type for your library. You can add content from YouTube or Vimeo.`} />
                    </Grid>

                    {/* YouTube */}
                    <Grid item xs={12} md={6}>
                        {createCard(<YouTubeIcon sx={{ fontSize: 24, color: "#FF0000", verticalAlign: "top" }} />, "YouTube", "Via URL", "#FF0000")}
                    </Grid>

                    {/* Vimeo */}
                    <Grid item xs={12} md={6}>
                        {createCard(<OndemandVideoIcon sx={{ fontSize: 24, color: "#1AB7EA", verticalAlign: "top" }} />, "Vimeo", "Via URL", "#1AB7EA", true)}
                    </Grid>
                </Grid>
            </Container>
        );
    };

    // Create the step components.
    const stepUrl = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment
                            value={`Please input the URL and the type of the library. The URL should be a direct link to the library.

                            For example, a YouTube channel would have a URL like "https://www.youtube.com/@...".`}
                        />
                    </Grid>

                    <Grid item xs={12} mb={2}>
                        <CustomInput name="url" row={row} onChange={onChange} label="URL" autoFocus />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const step2 = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment value={`Define additional details about the library.`} />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput name="name" row={row} onChange={onChange} label="Name" />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput name="description" row={row} onChange={onChange} label="Description" multiline minRows={2} maxRows={5} />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const step3 = () => {
        const placeholder = `# Netscape HTTP Cookie File
# http://curl.haxx.se/rfc/cookie_spec.html
# This file was generated by Cookie-Editor
...`;

        const type = cookieRow.type;

        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment
                            value={`Streaming platforms can sometimes flag suspicious IPs and might require authorization to view content. This is common when dealing with public hosting providers or VPNs.

                            This can often be resolved by providing valid cookies along with the request. The most recent cookie was created on ${
                                formatDate(recentCookie?.[type]?.created_on) || "N/A"
                            }.`}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput name="type" row={cookieRow} onChange={onCookieChange} label="Type" select={COOKIE_TYPES} />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput
                            name="text"
                            row={cookieRow}
                            onChange={onCookieChange}
                            label="Copy & paste cookies in Netscape format here"
                            multiline
                            minRows={4}
                            maxRows={4}
                            placeholder={placeholder}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput name="note" row={cookieRow} onChange={onCookieChange} label="Note" multiline minRows={2} maxRows={5} />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const step4 = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment
                            value={`Creating new library with the following details:
                            Name: ${row.name || "N/A"}
                            URL: ${row.url || "N/A"}

                            Click "Apply" to save the library or "Reset" to start over.`}
                        />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    // Define the steps and the final step.
    const steps = [
        {
            label: "Source",
            optional: false,
            component: step1(),
            nextDisabled: !sourceType
        },
        {
            label: "URL",
            optional: false,
            component: stepUrl(),
            nextDisabled: !formHasValue(row, "url") || formHasErrors(row, ["name", "description", "uploaded_on"]),
            visible: ["youtube", "vimeo"].includes(sourceType)
        },
        {
            label: "Additional details",
            optional: false,
            component: step2(),
            nextDisabled: formHasErrors(row),
            visible: sourceType !== "youtube"
        },
        {
            label: "Authorization",
            optional: false,
            component: step3(),
            nextDisabled: formHasErrors(row) || cookieRow?.text?.length < 1,
            visible: sourceType === "youtube" && status?.require_youtube_cookies && isEmpty(recentCookie?.[cookieRow.type])
        }
    ];

    const final = { component: step4() };

    return (
        <>
            <ConfirmationDialog
                {...props}
                options={{
                    enqueueDialog,
                    setOpen,
                    ...options
                }}
            />

            <CustomStepper steps={steps} final={final} callback={(confirm) => handleClick(confirm)} />
        </>
    );
}
